import {
  getPartnerProducts,
  getPartnerStatus,
} from '@/api/partner';
import { extractProfilePartnerState } from '@/utils/partnerMembership.js';

const partner = {
  namespaced: true,
  state: {
    status: null,
    products: null,
    loading: false,
  },
  mutations: {
    SET_STATUS: (state, status) => {
      state.status = status;
    },
    PATCH_STATUS: (state, patch) => {
      state.status = { ...(state.status || {}), ...patch };
    },
    SET_PRODUCTS: (state, products) => {
      state.products = products;
    },
    SET_LOADING: (state, loading) => {
      state.loading = loading;
    },
    CLEAR_SESSION: (state) => {
      state.status = null;
      state.products = null;
      state.loading = false;
    },
  },
  getters: {
    getStatus: (state) => state.status,
    getProducts: (state) => state.products,
    isLoading: (state) => state.loading,
    getHighlights: (state) => state.products?.highlights || [],
    getPointsEarn: (state) => state.products?.pointsEarn || null,
    getProfilePartnerState: (state) => extractProfilePartnerState(state.status),
    canPurchaseHighlight: (state) => Boolean(state.status?.canPurchaseHighlight),
    isDebugPartnerPremium: (state) => Boolean(state.status?.isDebugPartnerPremium),
    availablePoints: (state) => Number(state.status?.points?.available || 0),
  },
  actions: {
    applyStatusUpdate({ commit, dispatch }, detail = {}) {
      if (detail?.status) {
        commit('SET_STATUS', detail.status);
        return;
      }
      if (detail?.statusPatch) {
        commit('PATCH_STATUS', detail.statusPatch);
        return;
      }
      return dispatch('refreshStatus');
    },
    async refreshStatus({ commit, rootGetters }) {
      if (!rootGetters['profile/getCompanyID']) {
        commit('SET_STATUS', null);
        return null;
      }
      try {
        const status = await getPartnerStatus();
        commit('SET_STATUS', status);
        return status;
      } catch {
        commit('SET_STATUS', null);
        return null;
      }
    },
    async fetchProducts({ commit, state }) {
      try {
        const products = await getPartnerProducts();
        commit('SET_PRODUCTS', products);
        return products;
      } catch {
        return state.products || null;
      }
    },
    async load({ commit, rootGetters, state }) {
      if (!rootGetters['profile/getCompanyID']) {
        commit('CLEAR_SESSION');
        return;
      }
      commit('SET_LOADING', true);
      try {
        const products = await getPartnerProducts();
        commit('SET_PRODUCTS', products);
        const status = await getPartnerStatus();
        commit('SET_STATUS', status);
      } catch {
        commit('SET_PRODUCTS', state.products || null);
        commit('SET_STATUS', null);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async ensureLoaded({ dispatch, state, rootGetters, commit }) {
      if (!rootGetters['profile/getCompanyID']) {
        commit('CLEAR_SESSION');
        return;
      }
      if (!state.products) {
        return dispatch('load');
      }
      await Promise.all([
        dispatch('fetchProducts'),
        dispatch('refreshStatus'),
      ]);
    },
    clearSession({ commit }) {
      commit('CLEAR_SESSION');
    },
  },
};

export default partner;
